// ── Shipping tab (#623 B4 — read-only, Shopify is the only source) ──────────
//
// The brand sees, on a world map, which countries it ships to — pulled from its
// Shopify shipping settings — and, on clicking a covered country, the Shopify
// shipping methods a shopper would be offered there.
//
// USER DECISION (2026-09-22): the shipping fee has ONE source, Shopify. The
// former "your own Outfino price" editor (B5: brand_shipping_rates override) is
// gone from this tab; coverage and fees are changed in the Shopify Admin
// (Settings → Shipping and delivery) and flow back here via webhook, or on the
// Refresh button.
//
// Data
//   • GET  /shipping/coverage — covered countries + method list + the state of
//     the CACHED coverage (coverage_status / coverage_updated_at) that shopper
//     filters and the checkout gate run on. Shown as a status badge.
//   • POST /shipping/rates    — the country-specific Shopify methods (read).

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { geoCentroid } from 'd3-geo';
import worldTopo from 'world-atlas/countries-110m.json';
import {
  InfoCircle, GeoAlt, Shop, Truck, Plus, Dash, ArrowClockwise,
  CheckCircle, ExclamationTriangle, XCircle, BoxArrowUpRight,
} from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { fetchShippingCoverage, fetchShippingRates } from '../api/brands';
import { alpha2ToNumeric, numericToAlpha2, countryName } from '../config/countryCodes';
import { computeMapFit, MIN_ZOOM, MAX_ZOOM } from './shippingMapFit';
import { describeCoverageStatus } from './shippingCoverageStatus';
import './ShippingTab.scss';

const RATES_SUBTOTAL = 0;
const RATES_CURRENCY = 'EUR';

export default function ShippingTab({ brand }) {
  const { t } = useLocale();
  const [coverage, setCoverage] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [selected, setSelected] = useState(null);
  const [rates, setRates] = useState(null);
  const [ratesError, setRatesError] = useState(false);

  // Guards a stale response overwriting a newer one (brand switch / refresh).
  const requestRef = useRef(0);

  const loadCoverage = useCallback(({ initial } = {}) => {
    const id = ++requestRef.current;
    if (initial) {
      setCoverage(null);
      setLoadError(false);
      setSelected(null);
    } else {
      setRefreshing(true);
    }
    return fetchShippingCoverage(brand.id)
      .then((d) => { if (id === requestRef.current) setCoverage(d || {}); })
      .catch(() => { if (id === requestRef.current && initial) setLoadError(true); })
      .finally(() => { if (id === requestRef.current) setRefreshing(false); });
  }, [brand.id]);

  useEffect(() => {
    loadCoverage({ initial: true });
    return () => { requestRef.current += 1; };
  }, [loadCoverage]);

  const coveredNumeric = useMemo(() => {
    const set = new Set();
    for (const code of coverage?.countries || []) {
      const num = alpha2ToNumeric(code);
      if (num) set.add(num);
    }
    return set;
  }, [coverage]);

  const restOfWorld = !!coverage?.rest_of_world;

  function selectCountry(alpha2) {
    if (!alpha2) return;
    setSelected(alpha2);
    setRates(null);
    setRatesError(false);
    fetchShippingRates(brand.id, alpha2, { subtotal: RATES_SUBTOTAL, currency: RATES_CURRENCY })
      .then((methods) => setRates(Array.isArray(methods) ? methods : []))
      .catch(() => setRatesError(true));
  }

  if (coverage === null && !loadError) {
    return <div className="panel-card panel-card__muted">{t('shipping.loading')}</div>;
  }
  if (loadError) {
    return <div className="panel-card panel-card__muted">{t('shipping.loadError')}</div>;
  }

  const error = coverage?.error;
  // documents#637 — where the merchant reconnects: the Outfino app inside
  // their Shopify admin. Every coverage shape carries shopify_connection.
  const reauthUrl = coverage?.shopify_connection?.reauth_url || null;
  if (error === 'not_connected') {
    return (
      <div className="panel-card">
        <h2 className="panel-card__title">{t('shipping.title')}</h2>
        <ShippingNotice icon={<Shop size={20} />} title={t('shipping.notConnectedTitle')} body={t('shipping.notConnectedBody')} />
      </div>
    );
  }
  // The store's token itself was rejected (documents#637): the map cannot be
  // read until the merchant opens the app in the Shopify admin, which runs a
  // fresh token exchange.
  if (error === 'shopify_reauth_required') {
    return (
      <div className="panel-card">
        <h2 className="panel-card__title">{t('shipping.title')}</h2>
        <ShippingNotice
          icon={<ExclamationTriangle size={20} />}
          title={t('shipping.reauthTitle')}
          body={t('shipping.reauthBody')}
          action={<ReauthLink href={reauthUrl} />}
        />
        <CoverageStatusBadge coverage={coverage} />
      </div>
    );
  }
  if (error === 'shipping_access_denied') {
    // The token works but was never granted read_shipping — accepting the
    // permission prompt on the app's next open fixes it (scope_missing:*).
    const scopeMissing = typeof coverage?.reason === 'string' && coverage.reason.startsWith('scope_missing:');
    return (
      <div className="panel-card">
        <h2 className="panel-card__title">{t('shipping.title')}</h2>
        <ShippingNotice
          icon={<InfoCircle size={20} />}
          title={t(scopeMissing ? 'shipping.scopeMissingTitle' : 'shipping.accessDeniedTitle')}
          body={t(scopeMissing ? 'shipping.scopeMissingBody' : 'shipping.accessDeniedBody')}
          action={<ReauthLink href={reauthUrl} />}
        />
        <CoverageStatusBadge coverage={coverage} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="panel-card">
        <h2 className="panel-card__title">{t('shipping.title')}</h2>
        <ShippingNotice icon={<InfoCircle size={20} />} title={t('shipping.genericErrorTitle')} body={t('shipping.genericErrorBody')} />
        <CoverageStatusBadge coverage={coverage} />
      </div>
    );
  }

  const countryCount = coveredNumeric.size;
  const hasAnyCoverage = countryCount > 0 || restOfWorld;

  return (
    <div className="panel-card shipping">
      <div className="panel-card__row shipping__head">
        <h2 className="panel-card__title">{t('shipping.title')}</h2>
        <button
          type="button"
          className="btn-o btn-o--ghost btn-o--sm shipping__refresh"
          onClick={() => loadCoverage()}
          disabled={refreshing}
          aria-label={t('shipping.refresh')}
          title={t('shipping.refresh')}
        >
          <ArrowClockwise size={14} className={refreshing ? 'shipping__refresh-spin' : undefined} />
          {refreshing ? t('shipping.refreshing') : t('shipping.refresh')}
        </button>
      </div>

      <CoverageStatusBadge coverage={coverage} />

      <p className="shipping__source">
        <InfoCircle size={14} />
        <span>
          {t('shipping.source')}{' '}
          {t('shipping.sourceHowTo')}
        </span>
      </p>

      {!hasAnyCoverage ? (
        <div className="panel-card__muted">{t('shipping.noCoverage')}</div>
      ) : (
        <>
          <div className="shipping__summary">
            <span className="shipping__chip">
              <GeoAlt size={13} />
              {t('shipping.countryCount', { n: countryCount })}
            </span>
            {restOfWorld && (
              <span className="shipping__chip shipping__chip--row">{t('shipping.restOfWorldChip')}</span>
            )}
          </div>

          <div className="shipping__grid">
            <div className="shipping__map-wrap">
              <ShippingMap
                coveredNumeric={coveredNumeric}
                restOfWorld={restOfWorld}
                selected={selected}
                onSelect={selectCountry}
              />
              <div className="shipping__legend">
                <span className="shipping__legend-item">
                  <i className="shipping__swatch shipping__swatch--covered" />
                  {t('shipping.legendCovered')}
                </span>
                {restOfWorld && (
                  <span className="shipping__legend-item">
                    <i className="shipping__swatch shipping__swatch--row" />
                    {t('shipping.legendRestOfWorld')}
                  </span>
                )}
                <span className="shipping__legend-item">
                  <i className="shipping__swatch shipping__swatch--none" />
                  {t('shipping.legendNone')}
                </span>
              </div>
            </div>

            <aside className="shipping__panel">
              <CountryMethods
                selected={selected}
                rates={rates}
                ratesError={ratesError}
                restOfWorld={restOfWorld}
                covered={selected ? coveredNumeric.has(alpha2ToNumeric(selected)) : false}
                brandMethods={coverage?.methods || []}
              />
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

// ── Cached-coverage status badge (#623 coverage_status) ──────────────────────
//
// What shoppers are filtered on and what the checkout gate decides from is the
// CACHED coverage on the brand row, not the live read above — so the merchant
// must be able to see whether that cache is in step with Shopify.
function CoverageStatusBadge({ coverage }) {
  const { t } = useLocale();
  const { variant, key, vars } = describeCoverageStatus(coverage);
  const Icon = variant === 'ok' ? CheckCircle : variant === 'warn' ? ExclamationTriangle : XCircle;
  return (
    <div className={`shipping__status shipping__status--${variant}`} role="status">
      <Icon size={14} />
      <span>{t(key, vars)}</span>
    </div>
  );
}

// How far (in projected px) the pointer may move between press and release
// before we treat the gesture as a pan/drag rather than a country click. Keeps
// the map draggable without swallowing genuine taps.
const CLICK_DRAG_THRESHOLD = 5;

const ZOOM_STEP = 1.5;

function clampZoom(z) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

function ShippingMap({ coveredNumeric, restOfWorld, selected, onSelect }) {
  const { t } = useLocale();
  const selectedNumeric = selected ? alpha2ToNumeric(selected) : null;

  // ZoomableGroup pan+zoom state. `position` carries both centre and zoom so a
  // wheel/drag by the user and our auto-fit share one source of truth.
  const [position, setPosition] = useState({ coordinates: [0, 0], zoom: MIN_ZOOM });

  // Centroids of the covered countries, so the auto-fit can frame exactly what
  // the brand ships to. Built from the topojson features via d3-geo's
  // geoCentroid — no extra dependency, geometries already loaded.
  const targetCentroids = useMemo(() => {
    if (coveredNumeric.size === 0) return [];
    const geos = worldTopo?.objects?.countries
      ? // topojson: features come through Geographies, but for the fit we need
        // them up-front. Reuse the same feature list react-simple-maps builds.
        featuresFromTopo(worldTopo)
      : [];
    const pts = [];
    for (const geo of geos) {
      if (coveredNumeric.has(String(geo.id))) {
        const c = geoCentroid(geo);
        if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) pts.push(c);
      }
    }
    return pts;
  }, [coveredNumeric]);

  // Auto-fit whenever the coverage (target set) or rest_of_world changes.
  useEffect(() => {
    const { center, zoom } = computeMapFit(targetCentroids, { restOfWorld });
    setPosition({ coordinates: center, zoom });
  }, [targetCentroids, restOfWorld]);

  const zoomBy = useCallback((factor) => {
    setPosition((p) => ({ ...p, zoom: clampZoom(p.zoom * factor) }));
  }, []);

  // Drag-vs-click guard: remember where a press started and only fire onSelect
  // if the pointer barely moved (a tap, not a pan).
  const pressRef = useRef(null);
  const handleGeoPointerDown = useCallback((e) => {
    pressRef.current = { x: e.clientX, y: e.clientY };
  }, []);
  const wasDrag = useCallback((e) => {
    const start = pressRef.current;
    pressRef.current = null;
    if (!start) return false;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    return Math.hypot(dx, dy) > CLICK_DRAG_THRESHOLD;
  }, []);

  return (
    <div className="shipping__map-viewport">
      <ComposableMap projectionConfig={{ scale: 145 }} width={800} height={400} style={{ width: '100%', height: 'auto' }}>
        <ZoomableGroup
          center={position.coordinates}
          zoom={position.zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          onMoveEnd={(pos) => setPosition(pos)}
        >
          <Geographies geography={worldTopo}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const num = String(geo.id);
                const alpha2 = numericToAlpha2(num);
                const isCovered = coveredNumeric.has(num);
                const isRow = restOfWorld && !isCovered && !!alpha2;
                const isSelected = selectedNumeric && num === selectedNumeric;

                let cls = 'shipping-geo shipping-geo--none';
                if (isCovered) cls = 'shipping-geo shipping-geo--covered';
                else if (isRow) cls = 'shipping-geo shipping-geo--row';
                if (isSelected) cls += ' is-selected';

                // Only Shopify-covered countries open the method panel. RoW is
                // NOT individually covered, so it is view-only here.
                const clickable = isCovered;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    className={cls}
                    tabIndex={clickable ? 0 : -1}
                    role={clickable ? 'button' : undefined}
                    aria-label={geo.properties?.name}
                    style={{
                      default: { outline: 'none', cursor: clickable ? 'pointer' : 'default' },
                      hover: { outline: 'none', cursor: clickable ? 'pointer' : 'default' },
                      pressed: { outline: 'none' },
                    }}
                    onMouseDown={handleGeoPointerDown}
                    onClick={(e) => {
                      // Swallow the click that ends a pan so dragging never opens the panel.
                      if (wasDrag(e)) return;
                      if (clickable) onSelect(alpha2);
                    }}
                    onKeyDown={(e) => {
                      if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onSelect(alpha2);
                      }
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      <div className="shipping__zoom-controls">
        <button
          type="button"
          className="shipping__zoom-btn"
          onClick={() => zoomBy(ZOOM_STEP)}
          disabled={position.zoom >= MAX_ZOOM}
          aria-label={t('shipping.zoomIn')}
          title={t('shipping.zoomIn')}
        >
          <Plus size={18} />
        </button>
        <button
          type="button"
          className="shipping__zoom-btn"
          onClick={() => zoomBy(1 / ZOOM_STEP)}
          disabled={position.zoom <= MIN_ZOOM}
          aria-label={t('shipping.zoomOut')}
          title={t('shipping.zoomOut')}
        >
          <Dash size={18} />
        </button>
      </div>
    </div>
  );
}

// Turn the world-atlas topojson into the same GeoJSON features react-simple-maps
// renders, so the auto-fit can read their centroids up-front. Uses topojson-client,
// which react-simple-maps already depends on — no extra dependency.
function featuresFromTopo(topo) {
  const obj = topo?.objects?.countries;
  if (!obj) return [];
  // Lazy require keeps this out of the module's top-level (and out of the pure
  // fit module that Jest tests).
  // eslint-disable-next-line global-require
  const { feature } = require('topojson-client');
  const fc = feature(topo, obj);
  return fc?.features || [];
}

// ── Side panel: the Shopify methods for the selected country (read-only) ─────

function CountryMethods({ selected, rates, ratesError, restOfWorld, covered, brandMethods }) {
  const { t } = useLocale();

  const priceHiddenNames = useMemo(() => {
    const set = new Set();
    for (const m of brandMethods || []) if (m && m.price_shown === false && m.name) set.add(m.name);
    return set;
  }, [brandMethods]);

  if (!selected) {
    return (
      <div className="shipping__panel-empty">
        <GeoAlt size={22} />
        <p>{t('shipping.pickCountry')}</p>
      </div>
    );
  }

  const label = countryName(selected);

  return (
    <div className="shipping__methods">
      <div className="shipping__methods-head">
        <Truck size={16} />
        <span className="shipping__methods-country">{label}</span>
      </div>

      <div className="shipping__shopify-methods">
        <div className="shipping__subhead">{t('shipping.shopifyMethodsHead')}</div>
        {rates === null && !ratesError ? (
          <div className="panel-card__muted">{t('shipping.methodsLoading')}</div>
        ) : ratesError ? (
          <div className="panel-card__muted">{t('shipping.methodsError')}</div>
        ) : rates.length === 0 ? (
          <div className="shipping__methods-note">
            {covered || restOfWorld ? (
              <>
                {t('shipping.methodsEmptyButCovered')}{' '}
                {t('shipping.methodsEmptyCheckoutHint')}
              </>
            ) : t('shipping.methodsEmpty')}
          </div>
        ) : (
          <ul className="shipping__method-list">
            {rates.map((m, i) => {
              const priceHidden = priceHiddenNames.has(m.method);
              const price = formatPrice(m.price, m.currency);
              return (
                <li className="shipping__method" key={`${m.method || 'm'}-${i}`}>
                  <div className="shipping__method-main">
                    <span className="shipping__method-name">{m.method || '—'}</span>
                    {m.description && <span className="shipping__method-desc">{m.description}</span>}
                  </div>
                  <span className="shipping__method-price">
                    {priceHidden || price == null ? t('shipping.priceAtCheckout') : price}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function ShippingNotice({ icon, title, body, action = null }) {
  return (
    <div className="shipping__notice">
      <span className="shipping__notice-icon">{icon}</span>
      <div>
        <div className="shipping__notice-title">{title}</div>
        <div className="shipping__notice-body">{body}</div>
        {action}
      </div>
    </div>
  );
}

// documents#637 — "open the Outfino app in Shopify", to the merchant's own admin
// (shopify_connection.reauth_url). Nothing to render without a store domain.
function ReauthLink({ href }) {
  const { t } = useLocale();
  if (!href) return null;
  return (
    <a className="shipping__reauth" href={href} target="_blank" rel="noopener noreferrer">
      <span>{t('shipping.openShopifyApp')}</span>
      <BoxArrowUpRight size={12} />
    </a>
  );
}

function formatPrice(value, currency) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'EUR').toUpperCase(), maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${n.toLocaleString()}${currency ? ` ${currency}` : ''}`;
  }
}
